import { useState, useEffect, useCallback } from 'react';
import { Users, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';

interface GroupSummary {
  id: string;
  name: string;
  description?: string;
  membersCount: number;
}

interface ShareToGroupSectionProps {
  assignGroupUrl: string;
  onAssigned?: () => void;
}

export default function ShareToGroupSection({ assignGroupUrl, onAssigned }: ShareToGroupSectionProps) {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/me/groups', {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        setError(`Erreur serveur (${res.status})`);
        setGroups([]);
        return;
      }

      const json = await res.json();
      const arr: GroupSummary[] =
        Array.isArray(json?.data?.data) ? json.data.data :
        Array.isArray(json?.data) ? json.data :
        Array.isArray(json) ? json : [];

      setGroups(arr);
    } catch {
      setError('Erreur réseau');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const assignGroup = async (group: GroupSummary) => {
    setAssigningId(group.id);
    setMessage(null);
    try {
      const resp: any = await api.post(assignGroupUrl, { groupId: group.id });
      const data = resp?.data ?? resp;
      const assigned = data?.assigned ?? data?.sent ?? 0;
      const total = data?.total ?? group.membersCount;
      setMessage({
        type: 'ok',
        text: `${assigned} apprenant${assigned > 1 ? 's' : ''} ajouté${assigned > 1 ? 's' : ''} sur ${total} membre${total > 1 ? 's' : ''} du groupe « ${group.name} ».`,
      });
      onAssigned?.();
    } catch (err: any) {
      setMessage({ type: 'err', text: err?.message ?? 'Impossible de partager au groupe.' });
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <div className="border-t border-slate-200 pt-3 mt-3 dark:border-gray-700">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        <Users className="h-3.5 w-3.5" />
        Partager à un groupe
      </p>

      {loading && (
        <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin" />
          Chargement des groupes…
        </div>
      )}

      {!loading && error && (
        <p className="py-2 text-xs text-red-400">{error}</p>
      )}

      {!loading && !error && groups.length === 0 && (
        <p className="py-2 text-xs text-slate-400">
          Aucun groupe trouvé. Créez des groupes dans le dashboard institution.
        </p>
      )}

      {message && (
        <div className={`mb-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
          message.type === 'ok'
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
          <span>{message.text}</span>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div className="max-h-40 space-y-1.5 overflow-y-auto">
          {groups.map((g) => (
            <div
              key={g.id}
              className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-gray-800"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{g.name}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400">
                  {g.membersCount} membre{g.membersCount !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => assignGroup(g)}
                disabled={assigningId === g.id || g.membersCount === 0}
                className="inline-flex shrink-0 items-center gap-1 rounded-md bg-[#0C1C38] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#19335E] disabled:opacity-50"
              >
                {assigningId === g.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Users className="h-3 w-3" />
                )}
                Partager
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
